type Context = Record<string, string | number | boolean | null>
const endpoint = process.env.FRONTEND_REMOTE_LOG_URL
const sessionID = createDiagnosticID()
const responseContexts = new WeakMap<Response, Context>()
let sharedContext: Context = {}
let sentCount = 0
let delivering = false
const queue: object[] = []
export function createDiagnosticID() {
    const bytes = new Uint8Array(16)
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(bytes)
        return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}
export function diagnosticContext(context: Context) {
    sharedContext = { ...sharedContext, ...context }
}
export function diagnosticError(error: unknown): Context {
    const candidate = error as {
        errorCode?: string
        subError?: string
        correlationId?: string
        name?: string
        message?: string
    }
    const errorMessage = typeof error === 'string' ? error : candidate?.message
    const safeCode = (value: unknown) =>
        typeof value === 'string' && /^[a-zA-Z0-9_.:-]{1,128}$/.test(value) ? value : null
    return {
        errorType: safeCode(candidate?.name) || 'UnknownError',
        errorCode: safeCode(candidate?.errorCode),
        subError: safeCode(candidate?.subError),
        microsoftCorrelationID: safeCode(candidate?.correlationId),
        aadstsCode: typeof errorMessage === 'string' ? errorMessage.match(/AADSTS\d+/)?.[0] || null : null,
    }
}
export function logDiagnostic(event: string, details: Context = {}, level: 'INFO' | 'WARN' | 'ERROR' = 'INFO') {
    if (!endpoint || sentCount >= 500 || queue.length >= 100) return
    const context: Context = {
        ...sharedContext,
        ...details,
        component: 'outlook_rooms',
        event,
        sessionID,
        clientTimestamp: new Date().toISOString(),
    }
    for (const key of Object.keys(context)) {
        if (
            /^(.*token|password|secret|authorization|email|errorStack|componentStack|searchTerm|message|url)$/i.test(
                key,
            )
        )
            delete context[key]
    }
    queue.push({
        level,
        error: event,
        source: 'outlook-room-addin',
        feature: 'outlook_rooms',
        companyId: context.companyID,
        employeeId: context.employeeID,
        release: process.env.ADDIN_RELEASE || 'unversioned',
        context,
    })
    sentCount++
    void deliver()
}
async function deliver() {
    if (delivering) return
    delivering = true
    try {
        while (queue.length) {
            const payload = queue.shift()
            const controller = typeof AbortController === 'undefined' ? null : new AbortController()
            let timer: ReturnType<typeof setTimeout>
            const timeout = new Promise<never>((_, reject) => {
                timer = setTimeout(() => {
                    controller?.abort()
                    reject(new Error('Diagnostic delivery timed out.'))
                }, 5000)
            })
            try {
                const response = await Promise.race([
                    fetch(endpoint, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                        signal: controller?.signal,
                        keepalive: true,
                    }),
                    timeout,
                ])
                if (!response.ok) console.warn('Outlook diagnostic delivery rejected.', response.status)
            } catch {
                console.warn('Outlook diagnostic delivery failed.')
            } finally {
                clearTimeout(timer)
            }
        }
    } finally {
        delivering = false
    }
}
export function recordOfficeHost() {
    const diagnostics = Office.context.diagnostics
    const mailboxDiagnostics = Office.context.mailbox?.diagnostics
    diagnosticContext({
        host: String(diagnostics?.host || 'unknown'),
        platform: String(diagnostics?.platform || 'unknown'),
        hostVersion: diagnostics?.version || 'unknown',
        outlookClient: mailboxDiagnostics?.hostName || 'unknown',
        outlookHostVersion: mailboxDiagnostics?.hostVersion || 'unknown',
        nestedAppAuthSupported: Office.context.requirements.isSetSupported('NestedAppAuth', '1.1'),
    })
}
export async function diagnosticFetch(
    url: string,
    init: RequestInit,
    operation: string,
    destination: string,
    details: Context = {},
) {
    const requestID = createDiagnosticID()
    const start = Date.now()
    const context = { ...sharedContext, ...details, requestID, operation, destination }
    logDiagnostic('outlook_api_request_started', context)
    try {
        const response = await fetch(url, init)
        logDiagnostic(
            'outlook_api_response_received',
            {
                ...context,
                statusCode: response.status,
                backendRequestID: response.headers.get('x-yoffix-request-id'),
                appsyncRequestID: response.headers.get('x-amzn-requestid'),
                elapsedMs: Date.now() - start,
            },
            response.ok ? 'INFO' : 'ERROR',
        )
        responseContexts.set(response, context)
        return response
    } catch (error) {
        logDiagnostic(
            'outlook_api_request_failed',
            { ...context, elapsedMs: Date.now() - start, ...diagnosticError(error) },
            'ERROR',
        )
        throw error
    }
}
export async function readDiagnosticJSON<T>(response: Response): Promise<T> {
    try {
        return (await response.json()) as T
    } catch (error) {
        logDiagnostic(
            'outlook_api_request_failed',
            {
                ...responseContexts.get(response),
                reason: 'invalid_json_response',
                ...diagnosticError(error),
            },
            'ERROR',
        )
        throw error
    }
}
export function recordGraphQLResult(
    result: {
        data?: unknown
        errors?: Array<{ errorType?: string; extensions?: { code?: string }; message?: string }>
    },
    response: Response,
) {
    const context = responseContexts.get(response) || {}
    const failed = Boolean(result.errors?.length || !result.data)
    const firstError = result.errors?.[0]
    const errorCode = diagnosticError({ errorCode: firstError?.errorType || firstError?.extensions?.code }).errorCode
    logDiagnostic(
        failed ? 'outlook_graphql_request_failed' : 'outlook_graphql_request_succeeded',
        {
            ...context,
            graphqlErrorCount: result.errors?.length || 0,
            graphqlErrorType: errorCode,
            reason: result.errors?.length ? 'graphql_errors' : !result.data ? 'missing_data' : 'data_received',
            unauthorized: Boolean(
                firstError &&
                /unauthorized|token has expired/i.test(`${firstError.errorType || ''} ${firstError.message || ''}`),
            ),
        },
        failed ? 'ERROR' : 'INFO',
    )
}

export async function diagnosticAuthStep<T>(authPath: string, action: () => Promise<T>): Promise<T> {
    const stepID = createDiagnosticID()
    const context = { ...sharedContext, authPath, stepID }
    logDiagnostic('outlook_auth_step_started', context)
    const timer = setTimeout(
        () => logDiagnostic('outlook_auth_step_pending', { ...context, reason: 'no_result_after_30_seconds' }, 'WARN'),
        30000,
    )
    try {
        const result = await action()
        logDiagnostic('outlook_auth_step_succeeded', context)
        return result
    } catch (error) {
        logDiagnostic('outlook_auth_step_failed', { ...context, ...diagnosticError(error) }, 'ERROR')
        throw error
    } finally {
        clearTimeout(timer)
    }
}
